import { Box, Typography } from '@mui/material';
import { stageColourFor, stageLabelFor, tokens } from '../../theme';
import type { StageHeaderProps } from './types';

/**
 * The head of one column in the L1 grid: a grey bar naming the stage, with a 3px
 * rule in the stage's colour under it.
 *
 * **It is an `h2`, which is most of why it is a component.** The four bars are
 * the page's section headings — what a screen reader user navigates by to reach
 * "the Build phases" — so they carry heading semantics at 12px type rather than
 * the 24px MUI gives an `h2`.
 *
 * **The rule takes `onSurface` and not `fill`**, because nothing sits on it: it
 * is ink on the page, like a card's accent bar, where the rail's bar is a ground
 * under white. The label above it names the stage in grey, so the colour is
 * reinforcement and never the only signal (WCAG 1.4.1).
 */
export const StageHeader = ({ stage }: StageHeaderProps) => {
  const stageColour = stageColourFor(stage, 'onSurface');

  return (
    <Box
      sx={{
        borderTopLeftRadius: `${String(tokens.radius.md)}px`,
        borderTopRightRadius: `${String(tokens.radius.md)}px`,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          backgroundColor: 'surface.sunken',
          paddingBlock: 3,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Typography
          variant="caption"
          component="h2"
          sx={{
            fontWeight: 500,
            letterSpacing: '2px',
            color: 'text.secondary',
            whiteSpace: 'nowrap',
            margin: 0,
          }}
        >
          {stageLabelFor(stage)}
        </Typography>
      </Box>

      <Box sx={{ height: 3, backgroundColor: stageColour }} />
    </Box>
  );
};
